import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { API_PATHS } from '../config/api-paths';
import { BusinessCardApi, EligibleCardholder } from '../tokens/business-card.token';
import { LenderCardSummary } from '../tokens/lender-account.token';

/**
 * REST-only implementation of {@link BusinessCardApi} (epic EM-915 §FR-5).
 *
 * Calls:
 *   GET  /services/embedded/api/business/card/eligible-cardholders
 *   POST /services/embedded/api/business/card/requests  body: { formFactor, cardholderRepresentativeId }
 *
 * The endpoint is party-type-agnostic: the SME, Lender and E-D surfaces all use this one service
 * through the shared card-request component. The backend resolves the business from the session,
 * so no party id is ever sent from the client.
 *
 * Rule 07 / Rule 10 — no mock fallback.
 */
@Injectable()
export class BusinessCardService implements BusinessCardApi {
  private readonly http = inject(HttpClient);
  private readonly base = `${API_PATHS.EMBEDDED_API_BASE}/business/card`;

  listEligibleCardholders(): Observable<EligibleCardholder[]> {
    return this.http.get<EligibleCardholder[]>(`${this.base}/eligible-cardholders`);
  }

  requestCard(
    formFactor: 'VIRTUAL' | 'PHYSICAL',
    cardholderRepresentativeId: number | null,
    scaToken: string,
  ): Observable<LenderCardSummary> {
    // EM-1023 / I4 — cardholder selection is SCA-gated (`issue_card`, EM-1826). The token rides the
    // dedicated `X-SCA-Token` header, NEVER `Authorization` (that stays the Keycloak bearer) —
    // the same convention every other SCA-gated card operation uses.
    const headers = new HttpHeaders({ 'X-SCA-Token': scaToken });
    return this.http.post<LenderCardSummary>(
      `${this.base}/requests`,
      { formFactor, cardholderRepresentativeId },
      { headers },
    );
  }
}
